import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { hrmsGetRequest, hrmsPostRequest, hrmsUpdateRequest, hrmsDeleteRequest, hrmsPostRequestNoBody } from "../services/index";

const API_URL = "/api/assign-leaves"; // Updated to match backend route

// Fetch All Assigned Leaves
export const fetchAssignedLeaves = createAsyncThunk(
  "assignLeave/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(API_URL);
      console.log("Fetch All Assigned Leaves Response:", response.data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Fetch Assigned Leaves by Email
export const fetchAssignedLeavesByEmail = createAsyncThunk(
  "assignLeave/fetchByEmail",
  async (teacherEmail, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(`${API_URL}/teacher/${teacherEmail}`);
      console.log("Fetch Assigned Leaves by Email Response:", response.data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Assign Leave
export const assignLeave = createAsyncThunk(
  "assignLeave/assign",
  async (leaveData, { rejectWithValue }) => {
    try {
      const response = await hrmsPostRequest(API_URL, leaveData);
      console.log("Assign Leave Response:", response.data);
      return response.data;
    } catch (error) {
      // Handle specific error messages from backend
      const errorMessage = error.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// Update Assigned Leave
export const updateAssignedLeave = createAsyncThunk(
  "assignLeave/update",
  async ({ id, updatedData }, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(`${API_URL}/${id}`, updatedData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Delete Assigned Leave
export const deleteAssignedLeave = createAsyncThunk(
  "assignLeave/delete",
  async (id, { rejectWithValue }) => {
    try {
      await hrmsDeleteRequest(`${API_URL}/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Auto-assign all leave limits to a teacher
export const autoAssignAllLeaveLimits = createAsyncThunk(
  "assignLeave/autoAssign",
  async (teacherEmail, { rejectWithValue }) => {
    try {
      const response = await hrmsPostRequestNoBody(`${API_URL}/auto-assign/${teacherEmail}`);
      console.log("Auto-assign Response:", response.data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const assignLeaveSlice = createSlice({
  name: "assignLeave",
  initialState: {
    assignedLeaves: [],
    status: "idle",
    error: null,
  },
  reducers: {
    // Optional: Add a reducer to clear assigned leaves if needed
    clearAssignedLeaves: (state) => {
      state.assignedLeaves = [];
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Assigned Leaves
      .addCase(fetchAssignedLeaves.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchAssignedLeaves.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.assignedLeaves = action.payload;
        state.error = null;
      })
      .addCase(fetchAssignedLeaves.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch assigned leaves";
      })
      // Fetch Assigned Leaves by Email
      .addCase(fetchAssignedLeavesByEmail.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchAssignedLeavesByEmail.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.assignedLeaves = action.payload;
        state.error = null;
      })
      .addCase(fetchAssignedLeavesByEmail.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch assigned leaves by email";
      })
      // Assign Leave
      .addCase(assignLeave.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(assignLeave.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.assignedLeaves.push(action.payload);
        state.error = null;
      })
      .addCase(assignLeave.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to assign leave";
      })
      // Update Assigned Leave
      .addCase(updateAssignedLeave.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateAssignedLeave.fulfilled, (state, action) => {
        state.status = "succeeded";
        const index = state.assignedLeaves.findIndex((leave) => leave.id === action.payload.id);
        if (index !== -1) {
          state.assignedLeaves[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateAssignedLeave.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to update assigned leave";
      })
      // Delete Assigned Leave
      .addCase(deleteAssignedLeave.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(deleteAssignedLeave.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.assignedLeaves = state.assignedLeaves.filter((leave) => leave.id !== action.payload);
        state.error = null;
      })
      .addCase(deleteAssignedLeave.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to delete assigned leave";
      })
      // Auto-assign all leave limits
      .addCase(autoAssignAllLeaveLimits.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(autoAssignAllLeaveLimits.fulfilled, (state, action) => {
        state.status = "succeeded";
        // Add the new assignments to the state
        if (action.payload.assignments) {
          state.assignedLeaves.push(...action.payload.assignments);
        }
        state.error = null;
      })
      .addCase(autoAssignAllLeaveLimits.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to auto-assign leave limits";
      });
  },
});

export const { clearAssignedLeaves } = assignLeaveSlice.actions;

export default assignLeaveSlice.reducer;